/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { DestroyRef, Injectable, computed, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Store, select } from '@ngrx/store';
import { distinctUntilChanged } from 'rxjs/operators';
import { WindowResizeService } from '../../core/services';
import { UpdateIsFullscreen } from '../../core/store/actions';
import { getIsFullscreen, getIsManagePostedSpeedOpen, getIsReplayOpen } from '../../core/store/selectors/layout.selectors';
import { IAppState } from '../../core/store/state';
import { BASIC_SPEED_MS } from '../../maps/animations';
/**
 * Centralizes the manage posted speed panel open/close animation choreography.
 *
 * All CSS transitions are defined in the component stylesheets; this service
 * only manages the timing of class toggles and state changes.
 *
 * OPEN sequence (from the moment isManagePostedSpeedOpen becomes true):
 *   0ms                    – showPanel becomes true (adds component to DOM + manage-posted-speed-open class to host)
 *                            CSS handles: status panel collapses via fullscreen, overlay items slide up after delay
 *   BASIC_SPEED_MS * 2     – isPanelSlideIn / showControls become true (panel slides in, controls appear, map resizes)
 *
 * CLOSE sequence (from the moment isManagePostedSpeedOpen becomes false):
 *   0ms                    – isPanelSlideIn becomes false (panel slides out + map resizes back via CSS)
 *   BASIC_SPEED_MS         – showPanel becomes false (removes component + manage-posted-speed-open class)
 *                            CSS handles: overlay items slide back down
 *                            UpdateIsFullscreen(false) dispatched → status panel expands
 */
@Injectable({ providedIn: 'root' })
export class ManagePostedSpeedAnimationService {
  /** Whether the overlay component should be in the DOM and the host should have the open class */
  public readonly showPanel = signal(false);

  /** Whether the panel should have the slide-in class (triggers CSS transition) */
  public readonly isPanelSlideIn = signal(false);

  /** Whether the MPS controls (search bar) should be visible */
  public readonly showControls = computed(() => this.isPanelSlideIn());

  /** Observable version of showPanel for components that need it (e.g. MapComponent with OnPush) */
  public readonly showPanel$ = toObservable(this.showPanel);

  // Timer IDs for cancellation — protects against programmatic close (e.g., route change)
  // during the open animation, even though the UI close button isn't available until the animation completes.
  private _openTimerId: ReturnType<typeof setTimeout> | null = null;
  private _closeTimerId: ReturnType<typeof setTimeout> | null = null;
  private readonly _isMobileOrTablet = toSignal(this._windowResizeService.isMobileOrTablet$, { initialValue: false });
  private readonly _isFullscreen = toSignal(this._store.pipe(select(getIsFullscreen)), { initialValue: false });
  private readonly _isReplayOpen = toSignal(this._store.pipe(select(getIsReplayOpen)), { initialValue: false });

  constructor(
    private readonly _store: Store<IAppState>,
    private readonly _destroyRef: DestroyRef,
    private readonly _windowResizeService: WindowResizeService
  ) {
    this._store.pipe(select(getIsManagePostedSpeedOpen), distinctUntilChanged(), takeUntilDestroyed(this._destroyRef)).subscribe(isOpen => {
      if (isOpen) {
        this._open();
      } else if (this.showPanel()) {
        this._close();
      }
    });
  }

  private _open(): void {
    // Cancel any pending close timer
    if (this._closeTimerId) {
      clearTimeout(this._closeTimerId);
      this._closeTimerId = null;
    }

    this.showPanel.set(true);

    // Skip the navbar/status-panel collapse delay when already fullscreen (e.g. opened from Replay)
    const delay = this._isFullscreen() ? 0 : BASIC_SPEED_MS * 2;

    this._openTimerId = setTimeout(() => {
      this._openTimerId = null;
      this.isPanelSlideIn.set(true);
    }, delay);
  }

  private _close(): void {
    // Cancel any pending open timer
    if (this._openTimerId) {
      clearTimeout(this._openTimerId);
      this._openTimerId = null;
    }

    this.isPanelSlideIn.set(false);

    // On mobile/tablet, close immediately (no slide-out animation)
    if (this._isMobileOrTablet()) {
      this.showPanel.set(false);
      if (!this._isReplayOpen()) {
        this._store.dispatch(new UpdateIsFullscreen(false));
      }
    } else {
      this._closeDesktop();
    }
  }

  private _closeDesktop(): void {
    this._closeTimerId = setTimeout(() => {
      this._closeTimerId = null;
      this.showPanel.set(false);
      if (!this._isReplayOpen()) {
        this._store.dispatch(new UpdateIsFullscreen(false));
      }
    }, BASIC_SPEED_MS);
  }
}
